import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Fetch proposals for a project
export const fetchProjectProposals = createAsyncThunk(
  'projectProposals/fetchProjectProposals',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/proposals`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch proposals');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these new actions
export const releaseProjectProposal = createAsyncThunk(
  'projectProposals/releaseProjectProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/release`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to release proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const deleteProjectProposal = createAsyncThunk(
  'projectProposals/deleteProjectProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete proposal');
      }

      return { proposalId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const projectProposalsSlice = createSlice({
  name: 'projectProposals',
  initialState: {
    proposals: [],
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectProposals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectProposals.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals = action.payload.proposals;
      })
      .addCase(fetchProjectProposals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(releaseProjectProposal.fulfilled, (state, action) => {
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        showToast('Proposal released successfully', 'success');
      })
      .addCase(releaseProjectProposal.rejected, (state, action) => {
        showToast(action.payload || 'Failed to release proposal', 'error');
      })
      .addCase(deleteProjectProposal.fulfilled, (state, action) => {
        state.proposals = state.proposals.filter(p => p._id !== action.payload.proposalId);
        showToast('Proposal deleted successfully', 'success');
      })
      .addCase(deleteProjectProposal.rejected, (state, action) => {
        showToast(action.payload || 'Failed to delete proposal', 'error');
      });
  }
});

export const { clearError } = projectProposalsSlice.actions;
export default projectProposalsSlice.reducer; 